import React, { useContext, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { motion } from "framer-motion";
import configs from "../../utils/configs";
import { CreateRoomButton } from "./CreateRoomButton";
import { PWAButton } from "./PWAButton";
import { useFavoriteRooms } from "./useFavoriteRooms";
import { usePublicRooms } from "./usePublicRooms";
import { AuthContext } from "../auth/AuthContext";
import { createAndRedirectToNewHub } from "../../utils/phoenix-utils";
import { MediaGrid } from "../room/MediaGrid";
import { MediaTile } from "../room/MediaTiles";
import { scaledThumbnailUrlFor } from "../../utils/media-url-utils";
import { SocialBar } from "../home/SocialBar";
import { SignInButton } from "./SignInButton";
import { AppLogo } from "../misc/AppLogo";
import { isHmc } from "../../utils/isHmc";
import maskEmail from "../../utils/mask-email";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

export function HomePage() {
  const auth = useContext(AuthContext);
  const intl = useIntl();

  const { results: favoriteRooms } = useFavoriteRooms();
  const { results: publicRooms } = usePublicRooms();

  const sortedFavoriteRooms = Array.from(favoriteRooms).sort((a, b) => b.member_count - a.member_count);
  const sortedPublicRooms = Array.from(publicRooms).sort((a, b) => b.member_count - a.member_count);
  const wrapInBold = chunk => <b>{chunk}</b>;

  useEffect(() => {
    const qs = new URLSearchParams(location.search);

    if (qs.has("sign_in")) {
      const redirectUrl = new URL("/signin", window.location);
      redirectUrl.search = location.search;
      window.location = redirectUrl;
    } else if (qs.has("auth_topic")) {
      const redirectUrl = new URL("/verify", window.location);
      redirectUrl.search = location.search;
      window.location = redirectUrl;
    }

    if (qs.has("new")) {
      qs.delete("new");
      createAndRedirectToNewHub(null, null, true, qs);
    }
  }, []);

  const canCreateRooms = !configs.feature("disable_room_creation") || auth.isAdmin;
  const email = auth.email;

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 py-4 max-w-7xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10">
                <AppLogo />
              </div>
              <span className="text-lg font-semibold text-foreground">{configs.translation("app-name")}</span>
            </div>

            <div className="flex items-center gap-4">
              {auth.isSignedIn ? (
                <>
                  <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-muted rounded-lg">
                    <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                    <span className="text-sm text-muted-foreground">
                      <FormattedMessage
                        id="header.signed-in-as"
                        defaultMessage="Signed in as {email}"
                        values={{ email: maskEmail(email) }}
                      />
                    </span>
                  </div>
                  <button
                    onClick={auth.signOut}
                    className="px-4 py-2 text-sm font-medium text-accent-foreground bg-accent hover:bg-accent/90 rounded-lg transition-colors"
                  >
                    <FormattedMessage id="header.sign-out" defaultMessage="Sign Out" />
                  </button>
                </>
              ) : (
                <SignInButton />
              )}
            </div>
          </div>
        </div>
      </nav>

      <section className="container mx-auto px-4 py-16 md:py-24 max-w-7xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
        >
          {/* Left: Content Column */}
          <motion.div variants={itemVariants} className="flex flex-col gap-8">
            {/* Status Badge */}
            {auth.isSignedIn && (
              <div className="flex items-center gap-2 w-fit">
                <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                <span className="text-sm text-muted-foreground">
                  <FormattedMessage
                    id="header.signed-in-as"
                    defaultMessage="Signed in as {email}"
                    values={{ email: maskEmail(email) }}
                  />
                </span>
              </div>
            )}

            {/* Logo */}
            <motion.div variants={itemVariants} className="w-16 h-16">
              <AppLogo />
            </motion.div>

            {/* Heading */}
            <motion.div variants={itemVariants}>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-playfair font-bold text-foreground leading-tight text-balance">
                {configs.translation("app-name")}
              </h1>
            </motion.div>

            {/* Description */}
            <motion.p
              variants={itemVariants}
              className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-md"
            >
              {configs.translation("app-description")}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-start gap-4 pt-4">
              {canCreateRooms && <CreateRoomButton />}
              <PWAButton />
            </motion.div>
          </motion.div>

          {/* Right: Image Column */}
          <motion.div variants={imageVariants} className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-border bg-card">
              <img
                alt={intl.formatMessage(
                  {
                    id: "home-page.hero-image-alt",
                    defaultMessage: "Screenshot of {appName}"
                  },
                  { appName: configs.translation("app-name") }
                )}
                src={
                  configs.image("home_background") ||
                  "/placeholder.svg?height=600&width=600&query=virtual-meeting-space"
                }
                className="w-full h-auto object-cover"
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {sortedPublicRooms.length > 0 && (
        <section className="container mx-auto px-4 py-12 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-4xl font-playfair font-bold text-foreground mb-12 text-center">
              <FormattedMessage id="home-page.public--rooms" defaultMessage="Public Rooms" />
            </h2>
            <div className="bg-card rounded-2xl p-8 border border-border shadow-sm">
              <MediaGrid center>
                {sortedPublicRooms.map((room, index) => (
                  <motion.div
                    key={room.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true, margin: "-50px" }}
                    whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  >
                    <MediaTile
                      entry={room}
                      processThumbnailUrl={(entry, width, height) =>
                        scaledThumbnailUrlFor(entry.images.preview.url, width, height)
                      }
                    />
                  </motion.div>
                ))}
              </MediaGrid>
            </div>
          </motion.div>
        </section>
      )}

      {sortedFavoriteRooms.length > 0 && (
        <section className="container mx-auto px-4 py-12 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-4xl font-playfair font-bold text-foreground mb-12 text-center">
              <FormattedMessage id="home-page.favorite-rooms" defaultMessage="Favorite Rooms" />
            </h2>
            <div className="bg-card rounded-2xl p-8 border border-border shadow-sm">
              <MediaGrid center>
                {sortedFavoriteRooms.map((room, index) => (
                  <motion.div
                    key={room.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true, margin: "-50px" }}
                    whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  >
                    <MediaTile
                      entry={room}
                      processThumbnailUrl={(entry, width, height) =>
                        scaledThumbnailUrlFor(entry.images.preview.url, width, height)
                      }
                    />
                  </motion.div>
                ))}
              </MediaGrid>
            </div>
          </motion.div>
        </section>
      )}

      {isHmc() ? (
        <section className="container mx-auto px-4 py-12 max-w-7xl">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <SocialBar />
          </motion.div>
        </section>
      ) : null}
    </div>
  );
}
